import { ref, Ref } from 'vue';
import { IPreviewHeader, OutputFormat, PreviewHeaderType } from '../domains/entities/InventoryTemplate';
import { isSingleFieldOutputKey } from '../constants/inventoryTemplateConstants';

/**
 * useTemplateDragDropReorderの戻り値の型
 */
export interface UseTemplateDragDropReorderReturn {
  /** ドラッグ中のヘッダーID */
  draggedHeaderId: Ref<string | null>;
  /** ドラッグオーバー中のインデックス */
  dragOverIndex: Ref<number | null>;
  /** ドラッグ開始ハンドラー */
  handleDragStart: (event: DragEvent, header: IPreviewHeader) => void;
  /** ドラッグ終了ハンドラー */
  handleDragEnd: (event: Event) => void;
  /** ドラッグオーバーハンドラー */
  handleDragOver: (event: DragEvent, targetIndex: number) => void;
  /** ドロップハンドラー */
  handleDrop: (
    event: DragEvent,
    targetIndex: number,
    headers: IPreviewHeader[],
    onReorderMaster: (draggedKey: string, targetKey: string) => void,
    onReorderOutputData: (draggedKey: string, targetKey: string) => void,
    onMoveSingleFieldItem?: (draggedKey: string, insertBeforeKey: string | undefined) => void
  ) => void;
  /** ドロップ可能かどうかを判定 */
  canDropAt: (draggedHeader: IPreviewHeader, targetIndex: number, headers: IPreviewHeader[]) => boolean;
}

/**
 * テンプレート機能用のドラッグ&ドロップによる並び替えを管理するコンポーザブル
 * @param outputFormat - 現在選択中の出力形式（月別/データ種類別）。単一フィールド項目の
 *   挿入可否判定（グループ境界の判定）に、ヘッダーの並びから推測するのではなく実際の値を使う
 * @returns ドラッグ&ドロップ管理用の関数群
 */
export const useTemplateDragDropReorder = (outputFormat: Ref<OutputFormat>): UseTemplateDragDropReorderReturn => {
  const draggedHeaderId = ref<string | null>(null);
  const dragOverIndex = ref<number | null>(null);

  /**
   * ドラッグ開始ハンドラー
   * @param event - ドラッグイベント
   * @param header - ドラッグされるヘッダー
   */
  const handleDragStart = (event: DragEvent, header: IPreviewHeader): void => {
    draggedHeaderId.value = header.id;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', header.id);
    }
    const target = event.target as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.style.opacity = '0.5';
      }, 1);
    }
  };

  /**
   * ドラッグ終了ハンドラー
   * @param event - イベント
   */
  const handleDragEnd = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.opacity = '1';
    }
    draggedHeaderId.value = null;
    dragOverIndex.value = null;
  };

  /**
   * ドラッグオーバーハンドラー
   * @param event - ドラッグイベント
   * @param targetIndex - ターゲットインデックス
   */
  const handleDragOver = (event: DragEvent, targetIndex: number): void => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    dragOverIndex.value = targetIndex;
  };

  /**
   * グループの境界かどうかを判定（月別・データ種類別の両方に対応）
   * 月別形式の場合：年月が変わる位置のみが境界
   * データ種類別形式の場合：outputDataKeyが変わる位置のみが境界
   *
   * 出力形式は、ヘッダーの並びから推測するのではなく、呼び出し元から渡された実際の
   * outputFormatの値を使う。月別項目が1種類しか選ばれていない場合、並びだけからでは
   * 月別形式かデータ種類別形式か判別できない（どちらの形式でも同じ並びになる）ため。
   * @param targetIndex - ターゲットインデックス
   * @param headers - ヘッダーリスト
   * @returns 境界の場合true
   */
  const isGroupBoundary = (targetIndex: number, headers: IPreviewHeader[]): boolean => {
    const targetHeader = headers[targetIndex];
    if (!targetHeader) return false;

    // ターゲットが単一フィールド項目の場合は境界とみなす
    if (isSingleFieldOutputKey(targetHeader.outputDataKey)) return true;

    // ターゲットが最初の出力データ項目の場合は境界
    const firstOutputDataIndex = headers.findIndex((h) => h.type === PreviewHeaderType.OUTPUT_DATA);
    if (targetIndex === firstOutputDataIndex) return true;

    // 前の項目を取得
    const prevHeader = headers[targetIndex - 1];
    if (!prevHeader) return true;

    // 前の項目が単一フィールド項目の場合は境界
    if (isSingleFieldOutputKey(prevHeader.outputDataKey)) return true;

    // 前の項目がマスタ項目の場合は境界
    if (prevHeader.type === PreviewHeaderType.MASTER) return true;

    if (outputFormat.value === OutputFormat.BY_MONTH) {
      // 月別形式：年月が変わる位置のみが境界
      const targetYearMonth = targetHeader.yearMonth;
      const prevYearMonth = prevHeader.yearMonth;

      if (targetYearMonth && prevYearMonth) {
        return targetYearMonth.year !== prevYearMonth.year || targetYearMonth.month !== prevYearMonth.month;
      }
    } else {
      // データ種類別形式：outputDataKeyが変わる位置のみが境界
      return targetHeader.outputDataKey !== prevHeader.outputDataKey;
    }

    return false;
  };

  /**
   * ドロップ位置の検証
   * @param draggedHeader - ドラッグされているヘッダー
   * @param targetIndex - ターゲットインデックス
   * @param headers - ヘッダーリスト
   * @returns ドロップ可能かどうか
   */
  const canDropAt = (draggedHeader: IPreviewHeader, targetIndex: number, headers: IPreviewHeader[]): boolean => {
    const targetHeader = headers[targetIndex];
    if (!targetHeader) return false;

    // マスタ項目は出力データの左側にのみ配置可能
    if (draggedHeader.type === PreviewHeaderType.MASTER) {
      const firstOutputDataIndex = headers.findIndex((h) => h.type === PreviewHeaderType.OUTPUT_DATA);
      return targetIndex <= (firstOutputDataIndex === -1 ? headers.length : firstOutputDataIndex);
    }

    // 出力データは出力データセクション内でのみ移動可能
    if (draggedHeader.type === PreviewHeaderType.OUTPUT_DATA) {
      const firstOutputDataIndex = headers.findIndex((h) => h.type === PreviewHeaderType.OUTPUT_DATA);
      if (targetIndex < firstOutputDataIndex) return false;

      const draggedIsSingle = isSingleFieldOutputKey(draggedHeader.outputDataKey);
      const targetIsSingle = isSingleFieldOutputKey(targetHeader.outputDataKey);

      // 単一フィールド項目（現在庫）は年月グループの境界にのみドロップ可能
      if (draggedIsSingle) {
        return targetHeader.type === PreviewHeaderType.OUTPUT_DATA && isGroupBoundary(targetIndex, headers);
      }

      // 月別項目は月別項目としかドロップできない（単一フィールド項目の位置には移動不可）
      if (!draggedIsSingle && targetIsSingle) return false;

      return true;
    }

    return false;
  };

  /**
   * ドロップハンドラー
   * @param event - ドラッグイベント
   * @param targetIndex - ターゲットインデックス
   * @param headers - ヘッダーリスト
   * @param onReorderMaster - マスタ項目の並び替えコールバック
   * @param onReorderOutputData - 出力データの並び替えコールバック
   * @param onMoveSingleFieldItem - 単一フィールド項目の移動コールバック
   */
  const handleDrop = (
    event: DragEvent,
    targetIndex: number,
    headers: IPreviewHeader[],
    onReorderMaster: (draggedKey: string, targetKey: string) => void,
    onReorderOutputData: (draggedKey: string, targetKey: string) => void,
    onMoveSingleFieldItem?: (draggedKey: string, insertBeforeKey: string | undefined) => void
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    if (!draggedHeaderId.value) return;

    const draggedHeader = headers.find((h) => h.id === draggedHeaderId.value);
    if (!draggedHeader) return;

    if (!canDropAt(draggedHeader, targetIndex, headers)) {
      dragOverIndex.value = null;
      return;
    }

    const targetHeader = headers[targetIndex];
    if (!targetHeader) return;

    // マスタ項目の並び替え
    if (draggedHeader.type === PreviewHeaderType.MASTER && draggedHeader.masterKey && targetHeader.masterKey) {
      onReorderMaster(draggedHeader.masterKey, targetHeader.masterKey);
    }
    // 出力データの並び替え
    else if (draggedHeader.type === PreviewHeaderType.OUTPUT_DATA && draggedHeader.outputDataKey) {
      const draggedIsSingle = isSingleFieldOutputKey(draggedHeader.outputDataKey);
      const targetIsSingle = isSingleFieldOutputKey(targetHeader.outputDataKey);

      if (draggedIsSingle && onMoveSingleFieldItem) {
        // 単一フィールド項目を月別項目の位置に移動
        // ターゲットのIDをinsertBeforeKeyとして設定
        onMoveSingleFieldItem(draggedHeader.outputDataKey, targetHeader.id);
      } else if (!draggedIsSingle && targetHeader.outputDataKey && !targetIsSingle) {
        // 月別項目同士の並び替え
        onReorderOutputData(draggedHeader.outputDataKey, targetHeader.outputDataKey);
      }
    }

    draggedHeaderId.value = null;
    dragOverIndex.value = null;
  };

  return {
    draggedHeaderId,
    dragOverIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    canDropAt,
  };
};
